import React, { memo, useCallback, useMemo, useRef, useState } from 'react';
import { useWtrDispatch } from 'redux/hooks';
import classNames from 'classnames';

import { CSSTransition, TransitionGroup } from 'react-transition-group';

import {
  Information,
  Pound,
  SuccessSolid,
} from '@johnlewispartnership/wtr-ingredients/foundations/icons';
import Typography from '@johnlewispartnership/wtr-ingredients/foundations/typography';
import { resetError } from 'redux/modules/search-and-browse/actions/reset-error';
import ProductGrid from 'components/ProductGrid/ProductGrid';
import { SSRMediaQuery } from 'components/SSRMediaQuery';
import { BREAKPOINTS } from 'constants/grid';

import ProductSelectMobileScreen from 'components/Builder/ProductSelectMobileScreen';
import SearchAndBrowseLoadMoreButton from 'components/LoadMoreButton';
import { LegacyProductReference } from 'constants/products';
import DealBuilder from './DealBuilder';
import { DealBuilderContext } from './context';
import { BaseBuilder, BaseGroupItemPlaceholder } from './types';

import styles from './styles.scss';

const builderTransitionClassNames = {
  exit: styles.builderExit,
  exitActive: styles.builderExitActive,
};

const builderTransitionTimeout = { exit: 1000 };

export interface CountAndTotal {
  totalCount: number;
  productCount: number;
}

interface BuilderProps<T extends BaseBuilder = BaseBuilder> {
  alwaysOpen?: boolean;
  className?: string;
  builders?: T[];
  getProducts: (groupId: string) => LegacyProductReference[];
  GroupItemPlaceholder: BaseGroupItemPlaceholder;
  loading?: boolean;
  location: string;
  searchType: string;
  setProductCountAndTotal?: React.Dispatch<React.SetStateAction<CountAndTotal | null>>;
  taxonomyLevel: string;
  warning?: string;
  completeMessage?: string;
  initialMessage?: string;
  mobileHeader?: React.ReactElement | null;
  multipleMessage?: string;
  completeIcon?: React.ReactElement;
  defaultIcon?: React.ReactElement;
}

export type { BuilderProps };

const builderDefaultPropBuilders: BaseBuilder[] = [];
const defaultCompleteIcon = (
  <SuccessSolid size="large" data-testid="completed" className={styles.successIcon} />
);
const defaultValueIcon = <Pound size="large" className={styles.defaultIcon} />;

const Builder = ({
  alwaysOpen = false,
  className,
  location,
  builders = builderDefaultPropBuilders,
  getProducts,
  GroupItemPlaceholder,
  loading = false,
  completeMessage,
  initialMessage = '',
  mobileHeader = null,
  multipleMessage,
  completeIcon = defaultCompleteIcon,
  defaultIcon = defaultValueIcon,
  searchType,
  setProductCountAndTotal,
  taxonomyLevel,
  warning,
}: BuilderProps) => {
  const dispatch = useWtrDispatch();
  const resetErrorCb = useCallback(() => dispatch(resetError()), [dispatch]);
  const [selectedGroup, setSelectedGroup] = useState<string | null>(null);

  const dismissProductsScreen = useCallback(() => {
    setProductCountAndTotal?.(null);
    setSelectedGroup(null);
  }, [setProductCountAndTotal]);

  const modalDismissFocusTargetFallbackRef = useRef<HTMLDivElement>(null);
  const modalDismissFocusTargetRef = useRef<HTMLElement | null>(null);

  const handleFocusOnModalDismiss = useCallback(() => {
    modalDismissFocusTargetFallbackRef.current?.focus();

    if (modalDismissFocusTargetRef.current) {
      modalDismissFocusTargetRef.current.focus();
      modalDismissFocusTargetRef.current = null;
    }
  }, []);

  const setModalDismissFocusTargetRef = useCallback((element: HTMLElement | null) => {
    modalDismissFocusTargetRef.current = element;
  }, []);

  const mobileBuilderHandlers = useMemo(
    () => ({
      setModalDismissFocusTargetRef,
      setSelectedGroup,
      setProductCountAndTotal,
      dismissProductsScreen,
    }),
    [
      setModalDismissFocusTargetRef,
      setSelectedGroup,
      setProductCountAndTotal,
      dismissProductsScreen,
    ],
  );

  const builderTemplate = builders[0];

  return (
    <>
      <div className={classNames(styles.builderContainer, className)}>
        <div
          className={styles.scrollContainer}
          ref={modalDismissFocusTargetFallbackRef}
          tabIndex={-1}
        >
          <DealBuilderContext.Provider value={mobileBuilderHandlers}>
            <TransitionGroup component={null}>
              {builders.map(builder => (
                <CSSTransition
                  classNames={builderTransitionClassNames}
                  timeout={builderTransitionTimeout}
                  key={builder.builderId}
                >
                  <DealBuilder
                    alwaysOpen={alwaysOpen}
                    builder={builder}
                    GroupItemPlaceholder={GroupItemPlaceholder}
                    length={builders.length}
                    location={location}
                    completeMessage={completeMessage}
                    initialMessage={initialMessage}
                    multipleMessage={multipleMessage}
                    completeIcon={completeIcon}
                    defaultIcon={defaultIcon}
                  />
                </CSSTransition>
              ))}
            </TransitionGroup>
          </DealBuilderContext.Provider>
          {warning && (
            <div className={styles.cheapestItemWarning}>
              <Information className={styles.icon} />
              <Typography noMargins styleAs="paragraphSmall" className={styles.cheapestItemText}>
                {warning}
              </Typography>
            </div>
          )}
        </div>
      </div>
      <SSRMediaQuery maxWidth={BREAKPOINTS.lg}>
        {builderTemplate.groups.map(builderGroup => {
          const key = builderGroup.groupId;
          const isOpen = selectedGroup === builderGroup.groupId;
          const titleText = builderGroup.name || initialMessage;
          const products = getProducts(builderGroup.groupId);

          return (
            <ProductSelectMobileScreen
              key={key}
              isOpen={isOpen}
              onClickBack={dismissProductsScreen}
              onDismiss={handleFocusOnModalDismiss}
              titleText={titleText}
              mobileHeader={mobileHeader}
            >
              <ProductGrid
                products={products}
                loading={loading}
                searchType={searchType}
                taxonomyLevel={taxonomyLevel}
                resetError={resetErrorCb}
                LoadMoreButton={SearchAndBrowseLoadMoreButton}
                noModals
              />
            </ProductSelectMobileScreen>
          );
        })}
      </SSRMediaQuery>
    </>
  );
};

Builder.displayName = 'Builder';

export default memo(Builder);
